/**
 * 학생 "학습 기록": 과제 목록의 복제가 아니라 실제 저장된 제출·채점 결과와 복습 결과를 따로 보여 준다.
 * - 최초 풀이 점수(GET /attempts/:id/grading)와 복습·근거 재확인 결과(GET /me/review-tasks 의 history)를 섞거나 합산하지 않는다.
 * - 점수는 "점수 보기"를 눌렀을 때만 조회한다. 서버가 계산한 정오만 세고 화면은 채점하지 않는다.
 * - 조회 실패는 빈 기록과 다른 문구로 보이고 지난 표시를 숨긴다. 기록이 없으면 없다고 밝힌다.
 */
import { h, clear } from './dom.js';
import { currentGen, type Transport } from '../core/transport.js';
import type { AssignmentInfo } from '../core/types.js';
import { dayText, type ReviewTask } from './review-tasks.js';

const KST_TIME = { timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' } as const;
const when = (iso: string | null): string => (iso === null ? '' : new Intl.DateTimeFormat('ko-KR', KST_TIME).format(new Date(iso)));

export function mountLearningRecord(root: HTMLElement, t: Transport, list: readonly AssignmentInfo[], onOpen: (assignmentId: string) => void): void {
  const gen = currentGen();
  const live = () => gen === currentGen() && root.isConnected;
  const submitted = list.filter((a) => a.progress.status === 'submitted' && a.progress.attemptId !== null);
  const inProgress = list.filter((a) => a.progress.status !== 'submitted' && a.progress.status !== 'not-started');

  // 1) 최초 풀이 결과
  const first = h('section', { class: 'rtasks rrec', 'data-testid': 'rec-first', 'aria-label': '최초 풀이 결과' }, h('h3', {}, '최초 풀이 결과'),
    h('p', { class: 'ev-note' }, '처음 제출한 답의 채점이에요. 복습·근거 결과와 합치지 않아요.'));
  if (submitted.length === 0) first.append(h('p', { 'data-testid': 'rec-first-empty' }, '아직 제출한 과제가 없어서 기록이 없어요.'));
  const ul = h('ul', { class: 'alist' });
  for (const a of submitted) {
    const out = h('span', { class: 'ameta', role: 'status', 'data-testid': `rec-score-${a.id}` });
    const btn = h('button', { class: 'secondary', type: 'button', 'data-testid': `rec-score-btn-${a.id}`, onclick: async () => {
      btn.disabled = true; out.textContent = '불러오고 있어요';
      const r = await t.request('GET', `/attempts/${a.progress.attemptId}/grading`);
      if (!live()) return;
      if (r.kind !== 'http' || r.status !== 200) { btn.disabled = false; out.textContent = '점수를 받아 오지 못했어요. 기록이 없는 것이 아니에요. 다시 눌러 주세요.'; return; }
      const pts = (r.body['points'] as { state: string; result?: string }[]) ?? [];
      const ok = pts.filter((p) => p.state === 'graded' && p.result === 'correct').length;
      const bad = pts.filter((p) => p.state === 'graded' && p.result === 'incorrect').length;
      const pend = pts.filter((p) => p.state === 'pending').length;
      out.textContent = `지점 ${pts.length}개 중 맞음 ${ok} · 틀림 ${bad}${pend > 0 ? ` · 결과 대기 ${pend}` : ''}`;
      btn.textContent = '다시 보기';
      btn.disabled = false;
    } }, '점수 보기');
    ul.append(h('li', { 'data-testid': `rec-${a.id}` },
      h('span', { class: 'at' }, a.release.title === a.title ? a.title : `${a.release.title} · ${a.title}`),
      h('span', { class: 'ameta' }, `제출 ${when(a.progress.submittedAt)}`), out, btn,
      h('button', { class: 'secondary', type: 'button', onclick: () => onOpen(a.id) }, '결과 화면 열기')));
  }
  if (submitted.length > 0) first.append(ul);
  if (inProgress.length > 0) first.append(h('p', { class: 'ev-note', 'data-testid': 'rec-inprogress' }, `아직 제출하지 않은 과제 ${inProgress.length}개는 제출 전이라 점수가 없어요.`));

  // 2) 복습·근거 재확인 결과
  const rev = h('section', { class: 'rtasks rrec', 'data-testid': 'rec-review', 'aria-label': '복습 기록' }, h('h3', {}, '복습·근거 기록'),
    h('p', { class: 'ev-note' }, '오답 복습과 근거 재확인의 회차별 결과예요. 해설을 본 뒤의 응답은 따로 표시해요.'));
  const revBody = h('div', {}, h('p', { class: 'loading' }, '복습 기록을 불러오고 있어요'));
  rev.append(revBody);
  root.append(first, rev);
  void (async () => {
    const r = await t.request('GET', '/me/review-tasks');
    if (!live()) return;
    clear(revBody);
    if (r.kind !== 'http' || r.status !== 200) {
      revBody.append(h('p', { class: 'notice', role: 'alert', 'data-testid': 'rec-review-error' }, '복습 기록을 받아 오지 못했어요. 기록이 없는 것이 아니라 받아 오지 못한 상태예요.'));
      return;
    }
    const done = (r.body['tasks'] as ReviewTask[]).filter((x) => (x.history ?? []).length > 0);
    if (done.length === 0) { revBody.append(h('p', { 'data-testid': 'rec-review-empty' }, '아직 복습한 기록이 없어요.')); return; }
    const kinds = { gr: done.filter((x) => x.kind !== 'evidence-recall'), ev: done.filter((x) => x.kind === 'evidence-recall') };
    for (const [label, rows, tid] of [['GR 오답 복습', kinds.gr, 'gr'], ['근거 문제 복습', kinds.ev, 'evidence']] as const) {
      if (rows.length === 0) continue;
      revBody.append(h('h4', {}, label), h('ul', { class: 'alist', 'data-testid': `rec-review-${tid}` }, ...rows.map((x) => h('li', { 'data-testid': `rec-rv-${x.itemId}` },
        h('span', { class: 'at' }, `${x.releaseTitle} · ${x.displayNumber}번`),
        ...(x.history ?? []).map((e) => h('span', { class: 'ameta' }, `${e.round}회차 ${e.result === 'correct' ? '맞음' : '틀림'} · ${when(e.respondedAt)}${e.infoProvidedBefore ? ' · 정답·해설 제공 뒤 응답' : ''}`)),
        h('span', { class: 'ameta' }, x.state === 'done' ? '복습 완료' : `다음 복습 ${dayText(x.dueAt)}`)))));
    }
  })();
}
